import { EMPTY, Observable, forkJoin } from 'rxjs';
import { CommunicationService } from '../../../services/communication.service';
import { BaseDataObject, InstallationData, MeterDataObject, OldMeterData } from '../../facilities-form/facilities-form.domain';
import { mergeMap, map, defaultIfEmpty } from 'rxjs/operators';

export interface FacilityEndpoints {
  UpdateInstallationUrl: string;
  UpdateFacilityUrl: string;

  UpdateDeviceUrl?: string;
  CreateDeviceUrl?: string;

  UpdateMeterUrl: string;
  CreateMeterUrl: string;

  UpdateOldMeterUrl: string;
  CreateOldMeterUrl: string;
}

export abstract class BaseFacilityService {
  constructor(protected communicationService: CommunicationService, protected endpoints: FacilityEndpoints) {}

  public saveInstallation(installationId: string, data: InstallationData, facilityData: BaseDataObject[]): Observable<any> {
    return this.updateInstallation(installationId, data).pipe(mergeMap(() => this.saveFacilityData(facilityData, installationId)));
  }

  public saveMeterData(meterData: MeterDataObject[], installationId: string): Observable<any> {
    return forkJoin(meterData.map(meter => this.saveMeter(meter, installationId))).pipe(defaultIfEmpty([]));
  }

  public saveOldMeterData(meterData: MeterDataObject[], installationId: string): Observable<any> {
    return forkJoin(meterData.map(meter => this.saveOldMeter(meter, installationId))).pipe(defaultIfEmpty([]));
  }

  public saveAdditionalData(technicalData: any, installationId: string): Observable<any> {
    return EMPTY;
  }

  protected updateInstallation(installationId: string, data: InstallationData): Observable<any> {
    return this.communicationService.updateData(`${this.endpoints.UpdateInstallationUrl}=${installationId}`, data);
  }

  private saveFacilityData(facilityData: BaseDataObject[], installationId: string): Observable<any> {
    const saveRequests = facilityData.map(facility =>
      this.saveFacility(facility, installationId).pipe(
        mergeMap(savedFacilityId => (this.hasDevices ? this.saveDevices(this.getDevices(facility), savedFacilityId) : EMPTY))
      )
    );

    return forkJoin(saveRequests).pipe(defaultIfEmpty([]));
  }

  protected get hasDevices(): boolean {
    return true;
  }

  private saveFacility(facility: BaseDataObject, installationId: string): Observable<string> {
    const requestData = this.mapToFacilityRequest(facility);
    if (facility.installationId) {
      return this.communicationService
        .updateData(`${this.endpoints.UpdateFacilityUrl}=${facility.facilityId}`, requestData)
        .pipe(map(() => facility.facilityId));
    }

    requestData.installation_id = installationId;

    return this.createFacility(requestData);
  }

  private saveMeter(meter: MeterDataObject, installationId: string): Observable<any> {
    const requestData = this.mapToMeterRequest(meter);

    if (meter.installationId) {
      return this.communicationService.updateData(`${this.endpoints.UpdateMeterUrl}=${meter.meterId}`, requestData);
    }

    requestData.installation_id = installationId;

    return this.communicationService.postData(this.endpoints.CreateMeterUrl, requestData);
  }

  private saveOldMeter(meter: MeterDataObject, installationId: string): Observable<any> {
    const requestData = this.mapToOldMeterRequest(meter);

    if (meter.installationId) {
      return this.communicationService.updateData(`${this.endpoints.UpdateOldMeterUrl}=${meter.meterId}`, requestData);
    }

    requestData.installation_id = installationId;

    return this.communicationService.postData(this.endpoints.CreateOldMeterUrl, requestData);
  }

  protected saveDevices(data: any[], facilityId: string): Observable<string[]> {
    const saveRequests = data.filter(device => this.deviceFilter(device)).map(device => this.saveDevice(device, facilityId));

    return forkJoin(saveRequests).pipe(defaultIfEmpty([]));
  }

  protected deviceFilter(device: any): boolean {
    return true;
  }

  protected getDevices(facility: BaseDataObject): any[] {
    return [];
  }

  protected saveDevice(device: { facilityId: string; deviceId: string }, facilityId: string): Observable<any> {
    const request = this.mapToFacilityDeviceRequest(device);

    if (device.facilityId) {
      return this.communicationService.updateData(`${this.endpoints.UpdateDeviceUrl}=${device.deviceId}`, request);
    }

    request.facility_id = facilityId;

    return this.communicationService.postData(this.endpoints.CreateDeviceUrl, request);
  }

  protected mapToFacilityDeviceRequest(device: any): any {
    return null;
  }

  protected abstract createFacility(request: any): Observable<string>;

  protected abstract mapToFacilityRequest(facility: BaseDataObject): any;
  protected abstract mapToMeterRequest(data: MeterDataObject): any;
  protected abstract mapToOldMeterRequest(data: MeterDataObject): any;
}
